import { useResetTopAssetsMutation } from '@carboncopyco/data';
import type { TopAssetType, TopAssetsSort } from '@carboncopyco/types';
import { removeUndefinedValues } from '@carboncopyco/utils';
import constate from 'constate';
import { useCallback, useMemo, useState } from 'react';
import { commonTypeFilterOptions } from './consts';

type UseTopAssetsTableControlsProps = {
  defaultSort: TopAssetsSort;
};

const useTopAssetsTableControls = ({
  defaultSort,
}: UseTopAssetsTableControlsProps) => {
  const [sort, setSort] = useState<TopAssetsSort>(defaultSort);
  const [selectedTypes, setSelectedTypes] = useState<{
    [K in TopAssetType]?: boolean;
  }>({});

  const [resetTopAssets] = useResetTopAssetsMutation();

  const memoizedSelectedTypes = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(removeUndefinedValues(selectedTypes)).map((key) => [
          key,
          commonTypeFilterOptions.find((item) => item.id === key).label,
        ])
      ),
    [selectedTypes]
  );

  const memoizedSetSort = useCallback<(sort: TopAssetsSort) => void>(
    (sort) => {
      resetTopAssets();
      setSort(sort);
    },
    [resetTopAssets]
  );

  const memoizedSetSelectedTypes = useCallback<(type: TopAssetType) => void>(
    (type) => {
      resetTopAssets();
      return setSelectedTypes({
        ...selectedTypes,
        [type]: !selectedTypes[type],
      });
    },
    [selectedTypes, resetTopAssets]
  );

  return {
    sort,
    setSort: memoizedSetSort,
    selectedTypes: memoizedSelectedTypes,
    setSelectedTypes: memoizedSetSelectedTypes,
    resetSelectedTypes: () => {
      resetTopAssets();
      setSelectedTypes({});
    },
  };
};

export const [TableControlsProvider, useTableControls] = constate(
  useTopAssetsTableControls
);
